import dayjs, { type Dayjs } from 'dayjs';
import { formatDayStrForYMDHMS } from '@/constants';

/**
 * 多条件匹配更新
 * @param originalArray - 原始数组
 * @param matchCondition - 匹配条件（对象形式，键值对必须匹配数组项的属性）
 * @param updateData - 要更新的属性键值对
 * @returns 新数组
 */
export function updateArrayObjectByMultiCondition<
	T extends Record<string, any>,
	U extends Partial<T>,
	M extends Partial<Record<keyof T, T[keyof T]>>, // 匹配条件：T 的部分属性键值对
>(originalArray: T[], matchCondition: M, updateData: U): T[] {
	// 入参校验
	if (!Array.isArray(originalArray) || typeof matchCondition !== 'object' || matchCondition === null) {
		console.warn('入参格式错误：originalArray 必须是数组，matchCondition 必须是对象');
		return originalArray;
	}

	const matchEntries = Object.entries(matchCondition) as [keyof T, T[keyof T]][];

	// 遍历并返回新数组
	return originalArray.map((item) => {
		// 判断是否满足所有匹配条件
		const isMatch = matchEntries.every(([key, value]) => item[key] === value);
		if (isMatch) {
			return { ...item, ...updateData };
		}
		return item;
	});
}

/**
 * 【整数专属】数字补零格式化函数 - 仅对整数且小于 10 的数值补前导零
 * @param {string | number} value - 待处理的值（支持数字/数字字符串，如 5、'8'、'12.3'、'abc' 等）
 * @param {number} [zeroLength=2] - 补零的目标位数（默认补到2位，如 5 → '05'，9 → '09'）
 * @returns {string} 格式化后的字符串：
 *                  1. 非有效数字 → 返回空字符串
 *                  2. 小数 → 直接返回原数字的字符串形式（不补零）
 *                  3. 整数<10 → 补零到指定位数
 *                  4. 整数≥10 → 直接返回原数字的字符串形式
 */
export function formatIntWithZero(
	value: string | number, // 明确入参类型：字符串或数字
	zeroLength: number = 2, // 补零位数，默认值2，类型约束为数字
): string {
	// 明确返回值类型：字符串
	// 1. 将入参统一转换为数字（处理string/number类型，非数字会转为NaN）
	const num: number = Number(value);

	// 2. 容错：非有效数字（如'abc'、undefined）直接返回空字符串
	if (isNaN(num)) {
		return '';
	}

	// 3. 校验是否为整数：小数不补零，直接返回原数字的字符串形式
	if (!Number.isInteger(num)) {
		return String(num);
	}

	// 4. 核心逻辑：整数<10则补零，否则返回原数字字符串
	//    Array(zeroLength).join('0') → 生成指定长度的零字符串（如zeroLength=2 → '00'）
	//    slice(-zeroLength) → 取最后N位，确保补零后长度符合要求
	return num < 10 ? (Array(zeroLength).join('0') + num).slice(-zeroLength) : String(num);
}

/**
 * 将数字 1-10 转换为对应的中文汉字
 * @param num - 待转换的数字（支持数字/数字字符串，如 5、'8'、'10'）
 * @returns {string} 中文汉字（如 1→'一'，10→'十'）；非 1-10 有效整数返回空字符串
 */
export function convertNumToChinese(num: number | string): string {
	// 1. 统一转换为数字并校验有效性
	const number = Number(num);

	// 校验：必须是 1-10 之间的整数
	if (!Number.isInteger(number) || number < 1 || number > 10) {
		console.warn(`输入值 "${num}" 无效，仅支持 1-10 之间的整数`);
		return '';
	}

	// 2. 数字-中文映射表（索引 0 对应空，1-9 对应一-九，10 单独处理）
	const chineseMap: Record<number, string> = {
		1: '一',
		2: '二',
		3: '三',
		4: '四',
		5: '五',
		6: '六',
		7: '七',
		8: '八',
		9: '九',
		10: '十',
	};

	// 3. 核心转换逻辑
	return chineseMap[number];
}

/**
 * 根据数字字符串索引，从 ["百位", "十位", "个位"] 中获取对应汉字
 * @param indexStr - 索引字符串（仅支持 "0"、"1"、"2"）
 * @returns 对应汉字（如 "0"→"百位"）；无效索引返回空字符串
 */
export function getPositionName(indexStr: string | number): string {
	// 1. 定义位置数组（索引 0=百位，1=十位，2=个位）
	const positionArr = ['百位', '十位', '个位'];

	// 2. 容错校验：先转换为数字，再校验是否为 0-2 之间的整数
	const index = Number(indexStr);
	if (
		isNaN(index) || // 非数字字符串（如 "abc"）
		!Number.isInteger(index) || // 小数（如 "1.5"）
		index < 0 ||
		index > 2 // 超出 0-2 范围
	) {
		console.warn(`索引 "${indexStr}" 无效，仅支持 "0"、"1"、"2"`);
		return '';
	}

	// 3. 核心逻辑：根据索引取对应值
	return positionArr[index];
}

/**
 * 压缩图片
 * @param file 原始文件
 * @param quality 压缩质量（0-1）
 * @param maxWidth 最大宽度
 * @returns 压缩后的 Blob 文件
 */
export const compressImage = async (file: File, quality = 0.5, maxWidth = 1920): Promise<Blob> => {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = (e) => {
			const img = new window.Image();
			img.src = e.target?.result as string;
			img.onload = () => {
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				if (!ctx) return resolve(file);

				// 计算压缩后的尺寸（等比例缩放）
				let width = img.width;
				let height = img.height;
				// console.log('原始图片尺寸:', img.width, 'x', img.height);

				if (width > maxWidth || height > maxWidth) {
					const ratio = Math.min(maxWidth / width, maxWidth / height);
					width = Math.floor(width * ratio);
					height = Math.floor(height * ratio);
				}

				// console.log('压缩目标尺寸:', width, 'x', height);

				canvas.width = width;
				canvas.height = height;
				ctx.drawImage(img, 0, 0, width, height);

				// 转换为 Blob（兼容 jpg/png）
				canvas.toBlob(
					(blob) => {
						if (blob) {
							// console.log('压缩后文件大小:', blob.size, 'bytes', '=', (blob.size / 1024).toFixed(2), 'KB');
							// const compressionRate = ((1 - blob.size / (img.width * img.height * 4)) * 100).toFixed(2);
							// console.log('压缩率: ~', compressionRate, '%');

							// 保留原文件名称和类型
							const newFile = new File([blob], file.name, {
								type: file.type || 'image/jpeg',
							});
							resolve(newFile);
						} else {
							resolve(file);
						}
					},
					'image/jpeg',
					quality,
				);
			};
		};
	});
};

/**
 * 获取当前年份的总天数（平年365天/闰年366天）
 * 核心逻辑：计算当年1月1日 00:00:00 到 12月31日 23:59:59 的毫秒差值 → 转换为天数 + 1
 * @returns {number} 当前年份的总天数（365 或 366）
 */
export function getCurrentYearTotalDays() {
	// 1. 定位到当前年份的1月1日 00:00:00（当年起始时间）
	// startOf('year')：dayjs 内置 API，将日期重置为「年首」，时间部分为 00:00:00
	const firstDay: Dayjs = dayjs().startOf('year');

	// 2. 定位到当前年份的12月31日 23:59:59（当年结束时间）
	// endOf('year')：dayjs 内置 API，将日期重置为「年末」，时间部分为 23:59:59
	const lastDay: Dayjs = dayjs().endOf('year');

	// 3. 计算年首到年末的毫秒差值 → 转换为天数
	// lastDay - firstDay：dayjs 对象直接相减会返回毫秒数差值
	// 1000*60*60*24：1天的总毫秒数（1000毫秒=1秒，60秒=1分，60分=1小时，24小时=1天）
	// Math.floor：向下取整，避免因毫秒级精度问题（如23:59:59.999）导致小数
	const diffMsToDays = lastDay.diff(firstDay, 'day');

	// 4. 差值 + 1：因为「年首 - 年首」差值为0，但1月1日是当年第1天
	// 例如：2024年（闰年）diffMsToDays 为 365 → +1 后得到 366 天
	// 例如：2023年（平年）diffMsToDays 为 364 → +1 后得到 365 天
	const totalDays = diffMsToDays + 1;

	// 返回当前年的总天数
	return totalDays;
}

/**
 * 格式化秒级时间戳为日期字符串（入参是时间戳 ÷ 1000 后的结果）
 * @param timestampSec 秒级时间戳（支持 0/null/undefined 触发错误返回）
 * @param options 配置项（可选，支持自定义格式、错误返回文本）
 * @returns 格式化后的日期字符串 / 自定义错误文本（入参无效时）
 * @example
 * // 秒级时间戳（默认乘1000）
 * formatTimestamp(1735689600) → '2025-01-01 00:00:00'
 * // 毫秒级时间戳（关闭乘1000）
 * formatTimestamp(1735689600000, { multiplyBy1000: false }) → '2025-01-01 00:00:00'
 * // 自定义错误文本 + 不乘1000
 * formatTimestamp(null, { errorText: '暂无时间', multiplyBy1000: false }) → '暂无时间'
 */
export function formatTimestamp(
	timestampSec: number | string | null | undefined,
	options?: {
		format?: string; // 日期格式，默认 'YYYY-MM-DD HH:mm:ss'
		errorText?: string; // 错误时返回的自定义文本，默认 '-'
		multiplyBy1000?: boolean; // 是否乘以1000（适配秒级转毫秒级），默认 true
	},
): string {
	// 解构配置项，设置默认值
	const {
		format = formatDayStrForYMDHMS,
		errorText = '-',
		multiplyBy1000 = true, // 默认乘以1000（适配秒级时间戳）
	} = options || {};

	// 1. 入参校验：0/null/undefined 直接返回自定义错误文本
	if (!timestampSec) {
		return errorText;
	}

	// 2. 转换为数字（处理字符串类型的秒级时间戳，如 '0'/'1735689600'）
	const numTimestamp = Number(timestampSec);

	// 3. 校验是否为有效数字（排除 NaN/Infinity 等）
	if (isNaN(numTimestamp) || !isFinite(numTimestamp)) {
		console.warn('格式化时间戳失败：入参不是有效数字', timestampSec);
		return errorText;
	}

	// 4. 处理时间戳：根据配置决定是否乘以1000
	const finalTimestamp = multiplyBy1000 ? numTimestamp * 1000 : numTimestamp;

	// 5. 校验时间戳有效性（dayjs 已兼容 0 时间戳）
	if (!dayjs(finalTimestamp).isValid()) {
		console.warn('格式化时间戳失败：无效的时间戳', timestampSec);
		return errorText;
	}

	// 6. 格式化并返回
	return dayjs(finalTimestamp).format(format);
}
